import { useFetch } from "@hyper-fetch/react";

import { sdk } from "@/api/sdk";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { localizeHref } from "@/paraglide/runtime.js";

const linkClass = "underline underline-offset-2 transition-colors hover:text-foreground";

/** Opens beside the purchase, so reading it never loses a half-made choice. */
const NoticeLink = ({ href, children }: { href: string; children: string }) => (
  <a href={href} target="_blank" rel="noreferrer" className={linkClass}>
    {children}
  </a>
);

/**
 * What a buyer has to be told before they commit (art. 12a of the Consumer Rights Act): who sells
 * this, that it is a trader, who answers for what, how results are ordered, and the documents. The
 * seller stays unnamed until its details arrive, or if they never do, and the rest still shows.
 */
export function SellerNotice({ organizationId, className }: { organizationId: string; className?: string }) {
  const { data: seller } = useFetch(sdk.publicOffers.seller.$get.setQueryParams({ organizationId }), {
    dependencies: [organizationId],
  });
  const name = seller?.name || m.seller_notice_unnamed();
  const details = [
    seller?.address,
    seller?.taxId ? m.seller_notice_tax_id({ id: seller.taxId }) : null,
    seller?.registrationNumber ? m.seller_notice_registration({ number: seller.registrationNumber }) : null,
  ].filter(Boolean);

  return (
    <div className={cn("grid gap-1.5 text-xs leading-5 text-muted-foreground", className)} data-slot="seller-notice">
      <p>
        {m.seller_notice_sold_by({ name })}
        {details.length > 0 ? ` (${details.join(", ")})` : ""}. {m.seller_notice_trader()} {m.seller_notice_duties()}
      </p>
      <p className="flex flex-wrap gap-x-3 gap-y-1">
        <NoticeLink href={`${localizeHref("/terms")}#${m.legal_terms_ranking_anchor()}`}>{m.seller_notice_ranking()}</NoticeLink>
        <NoticeLink href={localizeHref("/terms")}>{m.seller_notice_terms()}</NoticeLink>
        <NoticeLink href={localizeHref("/privacy")}>{m.seller_notice_privacy()}</NoticeLink>
        {seller?.termsUrl ? <NoticeLink href={seller.termsUrl}>{m.seller_notice_seller_terms()}</NoticeLink> : null}
        {seller?.privacyUrl ? <NoticeLink href={seller.privacyUrl}>{m.seller_notice_seller_privacy()}</NoticeLink> : null}
      </p>
    </div>
  );
}
